import {
  getFusionResultLabel,
  normalizeFusionResultCode,
} from "lib/utils/fusionResult";

const INDEX_ROWS_PER_PAGE = 15;

function translateCover(i18n, key, defaultValue, options = {}) {
  if (typeof i18n?.t === "function") {
    return i18n.t(key, {
      defaultValue,
      ...options,
    });
  }

  return defaultValue;
}

function isEnglishLocale(i18nOrLocale) {
  const locale =
    typeof i18nOrLocale === "string"
      ? i18nOrLocale
      : i18nOrLocale?.resolvedLanguage || i18nOrLocale?.language || "";

  return String(locale).toLowerCase().startsWith("en");
}

function toText(value) {
  return String(value ?? "")
    .replace(/\r\n/g, "\n")
    .replace(/\s+\n/g, "\n")
    .replace(/\n\s+/g, "\n")
    .trim();
}

function toSingleLine(value) {
  return toText(value).replace(/\s+/g, " ").trim();
}

function formatDateValue(value) {
  return toSingleLine(value);
}

function formatYearMonthLabel(value) {
  const normalized = toSingleLine(value);
  const match = normalized.match(/^(\d{4})[-./](\d{2})/);

  if (match) {
    return `${match[1]}.${match[2]}`;
  }

  return normalized;
}

function chunkItems(items = [], chunkSize = INDEX_ROWS_PER_PAGE) {
  if (!Array.isArray(items) || !items.length) return [[]];

  const chunks = [];
  for (let index = 0; index < items.length; index += chunkSize) {
    chunks.push(items.slice(index, index + chunkSize));
  }
  return chunks;
}

function buildImageSectionLabel(imageCount, videoCount, i18n) {
  if (imageCount > 0 && videoCount > 0) {
    return translateCover(
      i18n,
      "projectDetail.projectReportEditor.ud1000Cover.imageSection.imageVideo",
      "\uC774\uBBF8\uC9C0\u00B7\uC601\uC0C1 \uC9C4\uB2E8",
    );
  }

  if (videoCount > 0) {
    return translateCover(
      i18n,
      "projectDetail.projectReportEditor.ud1000Cover.imageSection.video",
      "\uC601\uC0C1 \uC9C4\uB2E8",
    );
  }

  return translateCover(
    i18n,
    "projectDetail.projectReportEditor.ud1000Cover.imageSection.image",
    "\uC774\uBBF8\uC9C0 \uC9C4\uB2E8",
  );
}

function buildFacilityDisplayName(page) {
  return `${toSingleLine(page?.file?.facility_name)} ${toSingleLine(
    page?.file?.part_name,
  )}`.trim();
}

function getDisplayFusionLabel(file, i18n) {
  const variant = file?.file_type === "video" ? "video" : "image";
  const fusionValue = file?.fusion_result_code ?? file?.fusion_result;
  const fusionCode = normalizeFusionResultCode(fusionValue);

  if (isEnglishLocale(i18n)) {
    if (fusionCode === 2) return "Fault";
    if (fusionCode === 3) return "Recheck";
  }

  return getFusionResultLabel(
    fusionValue,
    i18n,
    {
      variant,
      fallback: toSingleLine(file?.fusion_result),
    },
  );
}

function getDisplayInspectionStatus(i18n) {
  if (isEnglishLocale(i18n)) {
    return "Done";
  }

  return translateCover(
    i18n,
    "projectDetail.projectReportEditor.ud1000Cover.index.inspectionCompleted",
    "\uC644\uB8CC",
  );
}

function buildParticipantSummary(reportPages = [], i18n) {
  const participants = Array.from(
    new Set(
      reportPages
        .map((page) => toSingleLine(page?.file?.measured_by))
        .filter(Boolean),
    ),
  );

  if (!participants.length) {
    return {
      countLabel: "",
      namesLabel: "",
    };
  }

  return {
    countLabel: translateCover(
      i18n,
      "projectDetail.projectReportEditor.ud1000Cover.participantCount",
      `{{count}}\uBA85`,
      {
        count: participants.length,
      },
    ),
    namesLabel: participants.join(", "),
  };
}

function buildFusionStats(reportPages = []) {
  const stats = {
    total: Array.isArray(reportPages) ? reportPages.length : 0,
    normalCount: 0,
    cautionCount: 0,
    abnormalCount: 0,
    reinspectionCount: 0,
    cautionFacilities: [],
    abnormalFacilities: [],
    reinspectionFacilities: [],
  };

  (reportPages || []).forEach((page) => {
    const code = normalizeFusionResultCode(
      page?.file?.fusion_result_code ?? page?.file?.fusion_result,
      null,
    );
    const facilityName = buildFacilityDisplayName(page);

    if (code === 0) {
      stats.normalCount += 1;
      return;
    }

    if (code === 1) {
      stats.cautionCount += 1;
      if (facilityName) stats.cautionFacilities.push(facilityName);
      return;
    }

    if (code === 2) {
      stats.abnormalCount += 1;
      if (facilityName) stats.abnormalFacilities.push(facilityName);
      return;
    }

    if (code === 3) {
      stats.reinspectionCount += 1;
      if (facilityName) stats.reinspectionFacilities.push(facilityName);
    }
  });

  return stats;
}

function buildDiagnosisResult(reportPages = [], i18n) {
  const {
    total,
    normalCount,
    cautionCount,
    abnormalCount,
    reinspectionCount,
  } = buildFusionStats(reportPages);

  if (!total) {
    return "";
  }

  if (reinspectionCount) {
    return translateCover(
      i18n,
      "projectDetail.projectReportEditor.ud1000Cover.diagnosisResult.withReinspection",
      "\uCD1D {{total}}\uAC74\uC758 \uC124\uBE44 \uC911\uC5D0\uC11C \uC815\uC0C1 {{normalCount}}\uAC74, \uC694\uC8FC\uC758 {{cautionCount}}\uAC74, \uC774\uC0C1 {{abnormalCount}}\uAC74, \uC7AC\uC810\uAC80 \uC694\uB9DD {{reinspectionCount}}\uAC74\uC774 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
      {
        total,
        normalCount,
        cautionCount,
        abnormalCount,
        reinspectionCount,
      },
    );
  }

  return translateCover(
    i18n,
    "projectDetail.projectReportEditor.ud1000Cover.diagnosisResult.withoutReinspection",
    "\uCD1D {{total}}\uAC74\uC758 \uC124\uBE44 \uC911\uC5D0\uC11C \uC815\uC0C1 {{normalCount}}\uAC74, \uC694\uC8FC\uC758 {{cautionCount}}\uAC74, \uC774\uC0C1 {{abnormalCount}}\uAC74\uC774 \uBC1C\uC0DD\uD588\uC2B5\uB2C8\uB2E4.",
    {
      total,
      normalCount,
      cautionCount,
      abnormalCount,
    },
  );
}

function buildAnalysisOpinionLines(reportPages = [], i18n) {
  const {
    cautionFacilities,
    abnormalFacilities,
    reinspectionFacilities,
  } = buildFusionStats(reportPages);
  const lines = [];

  if (cautionFacilities.length) {
    lines.push(
      translateCover(
        i18n,
        "projectDetail.projectReportEditor.ud1000Cover.analysisOpinion.cautionFacilities",
        "\uC694\uC8FC\uC758 \uC124\uBE44: {{facilities}}",
        {
          facilities: cautionFacilities.join(", "),
        },
      ),
    );
  }

  if (abnormalFacilities.length) {
    lines.push(
      translateCover(
        i18n,
        "projectDetail.projectReportEditor.ud1000Cover.analysisOpinion.abnormalFacilities",
        "\uC774\uC0C1 \uC124\uBE44: {{facilities}}",
        {
          facilities: abnormalFacilities.join(", "),
        },
      ),
    );
  }

  if (reinspectionFacilities.length) {
    lines.push(
      translateCover(
        i18n,
        "projectDetail.projectReportEditor.ud1000Cover.analysisOpinion.reinspectionFacilities",
        "\uC7AC\uC810\uAC80 \uC694\uB9DD \uC124\uBE44: {{facilities}}",
        {
          facilities: reinspectionFacilities.join(", "),
        },
      ),
    );
  }

  return lines;
}

export function buildProjectDefaultAnalysisResult({
  reportPages = [],
  i18n,
} = {}) {
  return [buildDiagnosisResult(reportPages, i18n), ...buildAnalysisOpinionLines(reportPages, i18n)]
    .filter(Boolean)
    .join("\n");
}

function buildIndexRows(reportPages, reportStartPageNumber, i18n) {
  return (reportPages || []).map((page, index) => ({
    id: page?.id || `ud1000-cover-index-row-${index + 1}`,
    order: index + 1,
    facilityName: buildFacilityDisplayName(page),
    inspectionStatus: getDisplayInspectionStatus(i18n),
    inspectionResult: getDisplayFusionLabel(page?.file, i18n),
    pageNumber: reportStartPageNumber + index,
  }));
}

export function buildUd1000PdfCoverPages({
  project,
  form,
  reportPages = [],
  i18n,
}) {
  const projectName =
    toSingleLine(form?.project_name) || toSingleLine(project?.project_name);
  const contractName =
    toSingleLine(form?.contract_name) || toSingleLine(project?.contract_name);
  const contractor =
    toSingleLine(form?.contractor) || toSingleLine(project?.contractor);
  const periodStart = formatDateValue(form?.period_start || project?.period_start);
  const periodEnd = formatDateValue(form?.period_end || project?.period_end);
  const periodEndYearMonth = formatYearMonthLabel(
    form?.period_end || project?.period_end,
  );
  const purpose = toText(form?.purpose || project?.purpose);
  const participants = toText(form?.participants || project?.participants);
  const analysisResult = toText(
    form?.analysis_result || project?.analysis_result,
  );
  const imageCount = reportPages.filter((page) => page?.file?.file_type === "image").length;
  const videoCount = reportPages.filter((page) => page?.file?.file_type === "video").length;
  const imageSection = buildImageSectionLabel(imageCount, videoCount, i18n);
  const { countLabel: participantCount, namesLabel: participantNames } =
    buildParticipantSummary(reportPages, i18n);
  const diagnosisResult = buildDiagnosisResult(reportPages, i18n);
  const analysisOpinionLines = buildAnalysisOpinionLines(reportPages, i18n);
  const participantText =
    participants || [participantCount, participantNames].filter(Boolean).join("\n");
  const analysisResultText =
    analysisResult || buildProjectDefaultAnalysisResult({ reportPages, i18n });
  const indexPageCount = Math.max(
    1,
    Math.ceil((reportPages?.length || 0) / INDEX_ROWS_PER_PAGE),
  );
  const coverPageCount = 2 + indexPageCount;
  const indexRows = buildIndexRows(reportPages, coverPageCount, i18n);
  const indexChunks = chunkItems(indexRows, INDEX_ROWS_PER_PAGE);

  return [
    {
      id: "ud1000-cover-intro",
      isUd1000TemplateCover: true,
      coverPageType: "intro",
      title: translateCover(
        i18n,
        "projectDetail.projectReportEditor.ud1000Cover.pageTitles.intro",
        "\uD45C\uC9C0",
      ),
      projectName,
      periodEndYearMonth,
      imageSection,
      contractor,
      logoSrc: "/images/mainLogo.png",
    },
    {
      id: "ud1000-cover-overview",
      isUd1000TemplateCover: true,
      coverPageType: "overview",
      title: translateCover(
        i18n,
        "projectDetail.projectReportEditor.ud1000Cover.pageTitles.overview",
        "\uC6A9\uC5ED \uAC1C\uC694",
      ),
      contractName,
      periodStart,
      periodEnd,
      contractor,
      participantText,
      participantCount,
      participantNames,
      purpose,
      analysisResultText,
      diagnosisResult,
      analysisOpinionLines,
      logoSrc: "/images/mainLogo.png",
    },
    ...indexChunks.map((rows, index) => ({
      id: `ud1000-cover-index-${index + 1}`,
      isUd1000TemplateCover: true,
      coverPageType: "index",
      showIndexCaption: index === 0,
      title:
        indexChunks.length > 1
          ? translateCover(
              i18n,
              "projectDetail.projectReportEditor.ud1000Cover.pageTitles.indexWithPage",
              "\uC735\uD569 \uC810\uAC80 \uBAA9\uB85D \uBC0F \uACB0\uACFC {{page}}",
              {
                page: index + 1,
              },
            )
          : translateCover(
              i18n,
              "projectDetail.projectReportEditor.ud1000Cover.pageTitles.index",
              "\uC735\uD569 \uC810\uAC80 \uBAA9\uB85D \uBC0F \uACB0\uACFC",
            ),
      rows,
      logoSrc: "/images/mainLogo.png",
    })),
  ];
}
